<template>
  <div class="container v-content-container popup-wrapper">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-list-alt"></i>
          <span v-if="selectedVenueId != null">
            {{
              `External venue${noDataExist ? "" : ": " + selectedVenue.name}`
            }}
          </span>
          <span v-else-if="!loading">
            {{ $label("event.new_event_venue", "New external venue") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs></navigation-tabs>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" id="event-venue-form">
        <section>
          <div class="row">
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-map-marker"></i>
                {{ $label("event.venue_address", "Address") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Venue name -->
                      <section class="col col-12">
                        <text-input
                          v-model="selectedVenue.name"
                          id="venue-name"
                          :label="$label('event.venue_name', 'Name')"
                          :placeholder="$label('event.venue_name', 'Name')"
                          :is-disabled="updating"
                          :has-error="hasError('name')"
                          :error="getError('name')"
                          @keyup.native.enter="createOrUpdateVenue"
                        />
                      </section>
                      <!-- END Venue name -->
                      <!-- BEGIN Venue country -->
                      <section class="col col-6">
                        <label class="label">
                          {{ $label("event.venue_country", "Country") }}
                        </label>

                        <label class="select">
                          <select
                            id="venue-country"
                            class="text-capitalize"
                            :disabled="updating"
                            v-model="countryId"
                          >
                            <option
                              class="text-capitalize"
                              v-for="country in countries"
                              :key="country.id"
                              :value="country.id"
                            >
                              {{ country.slug }}
                            </option>
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('country_id')" class="text-danger">
                          {{ getError("country_id") }}
                        </p>
                      </section>
                      <!-- END Venue country -->
                      <!-- BEGIN Venue state -->
                      <section class="col col-6">
                        <label class="label">
                          {{ $label("event.venue_state", "States") }}
                        </label>

                        <label class="select">
                          <select
                            id="venue-state"
                            :disabled="updating"
                            v-model="stateId"
                          >
                            <option
                              v-for="state in states"
                              :key="state.id"
                              :value="state.id"
                            >
                              {{ state.name }}
                            </option>
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('state_id')" class="text-danger">
                          {{ getError("state_id") }}
                        </p>
                      </section>
                      <!-- END Venue state -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Venue city-->
                      <section class="col col-8">
                        <text-input
                          v-model="selectedVenue.address.city"
                          id="venue-city"
                          :label="$label('event.venue_city', 'City')"
                          :placeholder="$label('event.venue_city', 'City')"
                          :has-error="hasError('city')"
                          :error="getError('city')"
                          :is-disabled="updating"
                          @keyup.native.enter="createOrUpdateVenue"
                        />
                      </section>
                      <!-- END Venue city -->
                      <!-- BEGIN Venue postcode -->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedVenue.address.post_code"
                          id="venue-postcode"
                          :label="$label('event.venue_postcode', 'Postcode')"
                          :placeholder="$label('event.postcode', 'Postcode')"
                          :has-error="hasError('post_code')"
                          :error="getError('post_code')"
                          :is-disabled="updating"
                          @keyup.native.enter="createOrUpdateVenue"
                        />
                      </section>
                      <!-- END Venue postcode -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Venue street-->
                      <section class="col col-8">
                        <text-input
                          v-model="selectedVenue.address.street_name"
                          id="venue-street-name"
                          :label="$label('event.venue_street', 'Street')"
                          :placeholder="$label('event.venue_street', 'Street')"
                          :has-error="hasError('street_name')"
                          :error="getError('street_name')"
                          :is-disabled="updating"
                          @keyup.native.enter="createOrUpdateVenue"
                        />
                      </section>
                      <!-- END Venue street -->
                      <!-- BEGIN Venue street number-->
                      <section class="col col-4">
                        <text-input
                          v-model="selectedVenue.address.street_number"
                          id="venue-street-number"
                          :label="$label('event.street_number', 'Number')"
                          :placeholder="$label('event.street_number', 'Number')"
                          :has-error="hasError('street_number')"
                          :error="getError('street_number')"
                          :is-disabled="updating"
                          @keyup.native.enter="createOrUpdateVenue"
                        />
                      </section>
                      <!-- END Venue street number -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Venue additional info -->
                      <section class="col col-12">
                        <label class="label">
                          {{
                            $label(
                              "event.venue_additional_info",
                              "Additional info"
                            )
                          }}
                        </label>
                        <label class="textarea">
                          <textarea
                            v-model="
                              selectedVenue.address.additional_information
                            "
                            :class="{
                              'text-input-error': hasError(
                                'additional_information'
                              )
                            }"
                            :placeholder="
                              $label(
                                'event.venue_additional_info',
                                'Additional info'
                              )
                            "
                            :disabled="updating"
                            id="venue-additional-info"
                            rows="3"
                          >
                          </textarea>
                        </label>
                      </section>
                      <!-- END Venue additional info -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
            <div class="col-sm-6 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-info"></i>
                {{ $label("event.venue_information", "Information") }}
              </h2>
              <p></p>
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Venue description -->
                      <section class="col col-12">
                        <label class="label">
                          {{ $label("event.description", "Description") }}
                        </label>
                        <label class="textarea">
                          <textarea
                            v-model="selectedVenue.description"
                            :placeholder="
                              $label('event.description', 'Description')
                            "
                            :disabled="updating"
                            id="venue-description"
                            rows="5"
                          >
                          </textarea>
                        </label>
                      </section>
                      <!-- END Venue description -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="EventVenues"
            :class="{ 'is-disabled': updating }"
            id="back-event-date-settings-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{ 'is-disabled': updating }"
            id="save-venue-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateVenue"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedVenueId == null
            }"
            id="delete-venue-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteVenue"
          >
          </danger-button>
        </div>
        <div class="row mb-4">
          <div class="col-xs-12">&nbsp;</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors";

import NavigationTabs from "@/components/Settings/Events/NavigationTabs.vue";
import ViewLoader from "@/components/Common/ViewLoader";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import Error404 from "@/components/Common/Error404";
import swal from "sweetalert";

export default {
  name: "EventVenueDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    TextInput,
    InfoButton,
    BackButton,
    DangerButton,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      countries: [],
      states: [],
      countryId: null,
      stateId: null,
      selectedVenue: {
        name: null,
        description: null,
        address: {
          country_id: null,
          state_id: null,
          post_code: null,
          city: null,
          street_name: null,
          additional_information: null
        }
      },
      selectedVenueId: null,
      validatedInputs: [
        "name",
        "country_id",
        "state_id",
        "city",
        "post_code",
        "street_name",
        "street_number",
        "additional_information"
      ]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedVenueId = vm.$route.params.venue_id;
      // Get countries
      vm.getCountries(() => {
        //
      });

      // Get Venue
      if (
        typeof vm.selectedVenueId !== "undefined" &&
        vm.selectedVenueId !== null
      ) {
        if (vm.$route.query.venue_created) {
          swal("Success!", "The venue was created.", "success").then(() => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          });
        }
        vm.getVenue(() => {
          //
        });
      }
    });
  },

  beforeRouteUpdate(to, from, next) {
    // Get countries
    this.getCountries(() => {
      //
    });

    // Get Venue
    if (
      typeof this.selectedVenueId !== "undefined" &&
      this.selectedVenueId !== null
    ) {
      this.getVenue(() => {
        //
      });
    }
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateVenue() {
      if (this.selectedVenueId == null) {
        this.createVenue();
      } else {
        this.updateVenue();
      }
    },

    getVenue() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedVenue = response.data.data;
          if (!this.selectedVenue.address) {
            this.selectedVenue.address = {
              post_code: null,
              city: null,
              street_name: null,
              additional_information: null,
              state_id: null,
              country_id: null
            };
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateVenue() {
      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("venues/" + this.selectedVenueId);

      Api()
        .put(endpoint, this.selectedVenue)
        .then(response => {
          this.selectedVenueId = response.data.data.id;
          this.getVenue();
        })
        .catch(error => {
          console.log(error);
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    createVenue() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint("venues");

      Api()
        .post(endpoint, this.selectedVenue)
        .then(response => {
          this.selectedVenueId = response.data.data.id;
          this.$router.push({
            name: "EventVenueDetails",
            params: {
              venue_id: this.selectedVenueId
            },
            query: { venue_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteVenue() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text:
          "The external venue will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "venues/" + this.selectedVenueId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "EventVenues",
                query: { venue_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete external venues.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getCountries() {
      this.loading = true;

      Api()
        .get("api/internal/v1/countries")
        .then(response => {
          this.countries = response.data.data;
          if (this.countryId === null && response.data.data.length) {
            this.countryId = response.data.data[0].id;
          }
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },

  watch: {
    countryId(state) {
      this.selectedVenue.address.country_id = state;
      const states = this.countries.find(country => country.id === state)
        .states;
      this.states = states;
      if (states.length) {
        this.stateId = states[0].id;
      }
    },
    stateId(state) {
      this.selectedVenue.address.state_id = state;
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-6 {
  margin-top: 1.8rem;
}

.toggle-button {
  width: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 3px solid #3aa94c;
  padding: 2px 5px;
  cursor: pointer;
}

.toggle-button * {
  cursor: pointer;
}

@media screen and (max-width: 600px) {
  .toggle-button {
    margin-top: 0;
    margin-bottom: 1.3rem;
  }
}
</style>
